import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import Header from 'components/Headers/Header';
import Footer from 'components/Footers/Footer';
import axios from 'axios';
import { apiURL1 } from '../../config';
import { toast } from 'react-toastify';
import { FaFilter, FaEye, FaSearch, FaTimes } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ActionLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [superAdmins, setSuperAdmins] = useState([]);

  // Filter states - Updated as requested
  const [filters, setFilters] = useState({
    actionType: '',
    userRole: '',
    startDate: '',
    endDate: '',
    userId: '',
    searchTerm: ''
  });

  // Action type color mapping
  const getActionTypeColor = (actionType) => {
    const colors = {
      'read': 'info',
      'create': 'success',
      'update': 'warning',
      'delete': 'danger'
    };
    return colors[actionType?.toLowerCase()] || 'secondary';
  };

  // Fetch super admins for userId filter
  const fetchSuperAdmins = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        return;
      }

      const response = await axios.get(`${apiURL1}/superadmin/getAllSuperAdmins?page=1&limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.superAdmins) {
        setSuperAdmins(response.data.superAdmins);
      }
    } catch (error) {
      console.error('Error fetching super admins:', error);
    }
  };

  // Fetch action logs
  const fetchActionLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('Authentication token not found');
        return;
      }

      const params = {
        page: currentPage,
        limit: logsPerPage,
        ...filters
      };

      const response = await axios.get(`${apiURL1}/superadmin/action-logs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params
      });

      if (response.data.success) {
        setLogs(response.data.data.logs || []);
        if (response.data.data.pagination) {
          setTotalPages(response.data.data.pagination.totalPages);
          setTotalLogs(response.data.data.pagination.totalLogs);
        }
      } else {
        toast.error(response.data.message || 'Failed to fetch action logs');
      }
    } catch (error) {
      console.error('Error fetching action logs:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch action logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  useEffect(() => {
    fetchActionLogs();
  }, [currentPage, filters]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      actionType: '',
      userRole: '',
      startDate: '',
      endDate: '',
      userId: '',
      searchTerm: ''
    });
    setCurrentPage(1);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // View log details
  const viewLogDetails = (log) => {
    setSelectedLog(log);
    setDetailModal(true);
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => paginate(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis1" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => paginate(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis2" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => paginate(totalPages)}>{totalPages}</PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Action Logs</h3>
                    <p className="text-sm mb-0 text-muted">
                      Monitor all system activities and user actions
                    </p>
                  </Col>
                  <Col xs="4" className="text-right">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <FaFilter className="mr-2" />
                      Filters
                    </Button>
                  </Col>
                </Row>

                {/* Filters Section - Updated background color */}
                {showFilters && (
                  <Row className="mt-4 p-3 bg-gradient-secondary rounded">
                    <Col md="2">
                      <FormGroup>
                        <Label for="searchTerm" className="form-control-label text-white">
                          Search
                        </Label>
                        <div className="input-group">
                          <Input
                            id="searchTerm"
                            placeholder="Search actions, emails..."
                            type="text"
                            value={filters.searchTerm}
                            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                          />
                          <div className="input-group-append">
                            <span className="input-group-text">
                              <FaSearch />
                            </span>
                          </div>
                        </div>
                      </FormGroup>
                    </Col>

                    <Col md="2">
                      <FormGroup>
                        <Label for="actionType" className="form-control-label text-white">
                          Action Type
                        </Label>
                        <Input
                          id="actionType"
                          type="select"
                          value={filters.actionType}
                          onChange={(e) => handleFilterChange('actionType', e.target.value)}
                        >
                          <option value="">All Types</option>
                          <option value="read">Read</option>
                          <option value="create">Create</option>
                          <option value="update">Update</option>
                          <option value="delete">Delete</option>
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="2">
                      <FormGroup>
                        <Label for="userRole" className="form-control-label text-white">
                          User Role
                        </Label>
                        <Input
                          id="userRole"
                          type="select"
                          value={filters.userRole}
                          onChange={(e) => handleFilterChange('userRole', e.target.value)}
                        >
                          <option value="">All Roles</option>
                          <option value="superadmin">Super Admin</option>
                          <option value="admin">Admin</option>
                          <option value="teacher">Teacher</option>
                          <option value="student">Student</option>
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="2">
                      <FormGroup>
                        <Label for="userId" className="form-control-label text-white">
                          User ID
                        </Label>
                        <Input
                          id="userId"
                          type="select"
                          value={filters.userId}
                          onChange={(e) => handleFilterChange('userId', e.target.value)}
                        >
                          <option value="">All Users</option>
                          {superAdmins.map((admin) => (
                            <option key={admin._id} value={admin._id}>
                              {admin.email}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="2">
                      <FormGroup>
                        <Label className="form-control-label text-white">Start Date</Label>
                        <Input
                          type="date"
                          value={filters.startDate}
                          onChange={(e) => handleFilterChange('startDate', e.target.value)}
                          className="form-control"
                        />
                      </FormGroup>
                    </Col>

                    <Col md="2">
                      <FormGroup>
                        <Label className="form-control-label text-white">End Date</Label>
                        <Input
                          type="date"
                          value={filters.endDate}
                          onChange={(e) => handleFilterChange('endDate', e.target.value)}
                          className="form-control"
                        />
                      </FormGroup>
                    </Col>

                    <Col md="12" className="d-flex justify-content-end mt-2">
                      <Button
                        color="light"
                        size="sm"
                        onClick={clearFilters}
                        title="Clear Filters"
                      >
                        <FaTimes className="mr-1" />
                        Clear Filters
                      </Button>
                    </Col>
                  </Row>
                )}
              </CardHeader>

              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading action logs...</p>
                  </div>
                ) : (
                  <>
                    {/* Summary Stats */}
                    <Row className="mb-4">
                      <Col md="3">
                        <div className="text-center p-3 bg-gradient-info rounded text-white">
                          <h4 className="text-white mb-0">{totalLogs}</h4>
                          <small>Total Logs</small>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="text-center p-3 bg-gradient-success rounded text-white">
                          <h4 className="text-white mb-0">
                            {logs.filter(log => log.actionType === 'create').length}
                          </h4>
                          <small>Create Actions (Page)</small>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="text-center p-3 bg-gradient-warning rounded text-white">
                          <h4 className="text-white mb-0">
                            {logs.filter(log => log.actionType === 'update').length}
                          </h4>
                          <small>Update Actions (Page)</small>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="text-center p-3 bg-gradient-danger rounded text-white">
                          <h4 className="text-white mb-0">
                            {logs.filter(log => log.actionType === 'delete').length}
                          </h4>
                          <small>Delete Actions (Page)</small>
                        </div>
                      </Col>
                    </Row>

                    {/* Logs Table */}
                    <div className="table-responsive">
                      <Table className="align-items-center table-flush" responsive>
                        <thead className="thead-light">
                          <tr>
                            <th scope="col">Action Name</th>
                            <th scope="col">Action Type</th>
                            <th scope="col">Performed By</th>
                            <th scope="col">Entity Type</th>
                            <th scope="col">Entity Name</th>
                            <th scope="col">User Friendly Message</th>
                            <th scope="col">Timestamp</th>
                            <th scope="col">Status Code</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.length > 0 ? (
                            logs.map((log, index) => (
                              <tr key={log._id || index}>
                                <td>
                                  <span className="font-weight-bold">
                                    {log.action || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <Badge
                                    color={getActionTypeColor(log.actionType)}
                                    className="text-uppercase"
                                  >
                                    {log.actionType || 'N/A'}
                                  </Badge>
                                </td>
                                <td>
                                  <div>
                                    <span className="font-weight-bold text-capitalize">
                                      {log.performedBy?.userRole || 'N/A'}
                                    </span>
                                    <br />
                                    <small className="text-muted">
                                      {log.performedBy?.userEmail || 'N/A'}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <span className="text-sm">
                                    {log.context?.entityDetails?.entityType || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-sm">
                                    {log.context?.entityDetails?.affectedEntities?.[0]?.entityName || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-sm">
                                    {log.userFriendlyMessage || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-sm">
                                    {log.timestamp ? formatTimestamp(log.timestamp) : 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      log.context?.responseStatus >= 200 && log.context?.responseStatus < 300
                                        ? 'success'
                                        : log.context?.responseStatus >= 400
                                          ? 'danger'
                                          : 'warning'
                                    }
                                  >
                                    {log.context?.responseStatus || 'N/A'}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => viewLogDetails(log)}
                                  >
                                    <FaEye />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="10" className="text-center py-4">
                                <div className="text-muted">
                                  <h5>No logs found</h5>
                                  <p>Try adjusting your filters or check back later.</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Row className="mt-4">
                        <Col className="d-flex justify-content-center">
                          <Pagination>
                            <PaginationItem disabled={currentPage === 1}>
                              <PaginationLink
                                onClick={() => paginate(1)}
                                first
                              />
                            </PaginationItem>
                            <PaginationItem disabled={currentPage === 1}>
                              <PaginationLink
                                onClick={() => paginate(currentPage - 1)}
                                previous
                              />
                            </PaginationItem>
                            {renderPaginationItems()}
                            <PaginationItem disabled={currentPage === totalPages}>
                              <PaginationLink
                                onClick={() => paginate(currentPage + 1)}
                                next
                              />
                            </PaginationItem>
                            <PaginationItem disabled={currentPage === totalPages}>
                              <PaginationLink
                                onClick={() => paginate(totalPages)}
                                last
                              />
                            </PaginationItem>
                          </Pagination>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </div>
        </Row>

        {/* Log Detail Modal */}
        <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
          <ModalHeader toggle={() => setDetailModal(false)}>
            Action Log Details
          </ModalHeader>
          <ModalBody>
            {selectedLog && (
              <div>
                <Row>
                  <Col md="6">
                    <h6 className="text-muted mb-2">Action Information</h6>
                    <p><strong>Action:</strong> {selectedLog.action}</p>
                    <p><strong>Action Type:</strong> {selectedLog.actionType}</p>
                    <p><strong>Performed By:</strong> {selectedLog.performedBy?.userRole} ({selectedLog.performedBy?.userEmail})</p>
                    <p><strong>Timestamp:</strong> {formatTimestamp(selectedLog.timestamp)}</p>
                    <p><strong>Status Code:</strong> {selectedLog.context?.responseStatus}</p>
                  </Col>
                  <Col md="6">
                    <h6 className="text-muted mb-2">Entity Details</h6>
                    <p><strong>Entity Type:</strong> {selectedLog.context?.entityDetails?.entityType || 'N/A'}</p>
                    <p><strong>Entity Name:</strong> {selectedLog.context?.entityDetails?.affectedEntities?.[0]?.entityName || 'N/A'}</p>
                    <p><strong>User Friendly Message:</strong> {selectedLog.userFriendlyMessage || 'N/A'}</p>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="12">
                    <h6 className="text-muted mb-2">Additional Context</h6>
                    <pre className="bg-light p-3 rounded">
                      {JSON.stringify(selectedLog.context, null, 2)}
                    </pre>
                  </Col>
                </Row>
              </div>
            )}
          </ModalBody>
        </Modal>
      </Container>
      <Footer />
    </>
  );
};

export default ActionLogs;